import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Textarea } from '../ui/textarea';

export const MentionInput = ({
  value,
  onChange,
  onSubmit,
  placeholder = 'Add a comment...',
  users = [],
  className = '',
  rows = 2,
  ...props
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [mentionQuery, setMentionQuery] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const textareaRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Extract mention query from text
  const getMentionQuery = useCallback((text, position) => {
    const textBeforeCursor = text.slice(0, position);
    const match = textBeforeCursor.match(/@(\w*)$/);
    return match ? match[1] : null;
  }, []);

  // Handle input change
  const handleChange = (e) => {
    const newValue = e.target.value;
    const position = e.target.selectionStart;
    
    onChange(newValue);
    setCursorPosition(position);
    
    const query = getMentionQuery(newValue, position);
    
    if (query !== null) {
      setMentionQuery(query);
      const filtered = users.filter(user =>
        user.name.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 5);
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
      setSelectedIndex(0);
    } else {
      setShowSuggestions(false);
    }
  };

  // Insert mention
  const insertMention = (user) => {
    const textBeforeMention = value.slice(0, cursorPosition).replace(/@\w*$/, '');
    const textAfterMention = value.slice(cursorPosition);
    const newText = `${textBeforeMention}@${user.name} ${textAfterMention}`;
    
    onChange(newText);
    setShowSuggestions(false);
    
    // Focus back on textarea
    setTimeout(() => {
      if (textareaRef.current) {
        const newPosition = textBeforeMention.length + user.name.length + 2;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSubmit?.();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
      case 'Tab':
        e.preventDefault();
        if (suggestions[selectedIndex]) {
          insertMention(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target) &&
        !textareaRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`resize-none ${className}`}
        rows={rows}
        {...props}
      />
      
      {/* Mention hint */}
      {!showSuggestions && value.length === 0 && (
        <p className="text-xs text-slate-400 mt-1">
          Tip: Use @username to mention someone
        </p>
      )}
      
      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 overflow-hidden"
          data-testid="mention-suggestions"
        >
          {suggestions.map((user, index) => (
            <button
              key={user.user_id}
              onClick={() => insertMention(user)}
              className={`w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-slate-50 transition-colors ${
                index === selectedIndex ? 'bg-slate-100' : ''
              }`}
              data-testid={`mention-option-${user.user_id}`}
            >
              <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full rounded-full" />
                ) : (
                  user.name.charAt(0).toUpperCase()
                )}
              </div>
              <span className="text-sm text-slate-700">{user.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Utility to render text with highlighted mentions
export const renderTextWithMentions = (text) => {
  if (!text) return null;
  
  const parts = text.split(/(@\w+)/g);
  
  return parts.map((part, index) => {
    if (part.startsWith('@')) {
      return (
        <span
          key={`mention-${part}-${index}`}
          className="text-orange-500 font-medium hover:underline cursor-pointer"
          onClick={() => {
            // Could navigate to user profile here
            console.log('Clicked mention:', part);
          }}
        >
          {part}
        </span>
      );
    }
    return <span key={`text-${index}`}>{part}</span>;
  });
};

export default MentionInput;

